/*
 * @Author: AI Assistant
 * @Date: 2024-12-30 16:00:00
 * @LastEditors: AI Assistant
 * @LastEditTime: 2024-12-30 16:00:00
 * @FilePath: /RetailApp/retail-global-h5/src/pages/CPS/index.tsx
 * @Description: CPS订单查询页面 - 用户可以通过手机号后4位查询CPS订单信息
 * @测试页面链接查询串说明: https://s-test.g.mi.com/RetailGlobal/CPS?idcRegion=sg&saOrderId=SA1256431004231795&nonce=3fb716cb4bb6451bb7382277e27a9517&timeStamp=1764217548736&sign=BF3901F1C0EA11D817ABE00B10B76F82&language=zh-CN
 * @参数说明:
 * saOrderId (string, 必填) - 原始 SA 单号
 * phoneLast4 (string, 必填) - 手机号后四位 (纯数字)
 * idcRegion (string, 必填) - 机房区域 (比如sg（新加坡）、eu（欧洲），用以区分调用哪个接口)
 * timeStamp (long, 必填) - 链接签发时间戳 (毫秒)
 * nonce (string, 必填) - 随机串
 * sign (string, 必填) - HMAC_SHA512(secret, saOrderId + idcRegion + timestamp + nonce)
 */
import { memo, useState, useEffect } from 'react';
import intl from 'react-intl-universal';
import globalStyles from '../../global.less';
import styles from './index.less';
import { setTitleDesc } from '../../../utils/tools';
import Toast from './components/Toast';
import OrderCard from './components/OrderCard';
import LogisticsTrackModal from './components/LogisticsTrackModal';
import { verifyCPSOrder, ExtendedWaybillInfo } from '../../../api/retailGlobal/CPS';
import UrlParam from '../../../utils/UrlParam';

function CPSPage() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<ExtendedWaybillInfo[]>([]);
  const [showLogisticsModal, setShowLogisticsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ExtendedWaybillInfo | null>(null);
  const [orderItemId, setOrderItemId] = useState<string>('');
  const [urlParams, setUrlParams] = useState<{
    saOrderId?: string;
    idcRegion?: string;
    timeStamp?: string;
    nonce?: string;
    sign?: string;
  }>({});

  useEffect(() => {
    setTitleDesc(intl.get('cps.pageTitle'), intl.get('cps.pageTitle'));
    
    const search = window?.location?.search || '';
    if (search) {
      const params = UrlParam.parseUrlQueryString(search) as Record<string, string>;
      setUrlParams({
        saOrderId: params.saOrder || params.saOrderId,
        idcRegion: params.idcRegion || 'sg', // 默认使用新加坡机房
        timeStamp: params.timeStamp,
        nonce: params.nonce || params.randomCode,
        sign: params.sign
      });
    }
  }, []);

  const validateInputs = () => {
    const digitRegex = /^\d{4}$/;
    
    if (!phoneNumber) {
      Toast.error(intl.get('cps.errors.phoneEmpty'));
      return false;
    }
    
    if (!digitRegex.test(phoneNumber)) {
      Toast.error(intl.get('cps.errors.phoneFormat'));
      return false;
    }

    return true;
  };

  const handleQuery = async () => {
    if (!validateInputs()) return;
    
    if (!urlParams.saOrderId || !urlParams.idcRegion || !urlParams.timeStamp || !urlParams.nonce || !urlParams.sign) {
      Toast.error(intl.get('cps.errors.invalidUrl') || 'URL参数不完整，请重新扫描二维码');
      return;
    }

    setLoading(true);
    try {
      const response = await verifyCPSOrder([{
        saOrderId: urlParams.saOrderId!,
        phoneLast4: phoneNumber,
        idcRegion: urlParams.idcRegion!,
        timeStamp: urlParams.timeStamp!,
        nonce: urlParams.nonce!,
        sign: urlParams.sign!
      }]);
      
      if (response.code === 0) {
        const ordersWithPhone = response.data.waybillList.map(waybill => ({
          ...waybill,
          phoneLast4: phoneNumber
        }));
        
        setOrders(ordersWithPhone);
        if (ordersWithPhone.length === 0) {
          Toast.error(intl.get('cps.errors.notFound'));
        }
      } else {
        Toast.error(response.message || intl.get('cps.errors.queryFailed'));
        setOrders([]);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : intl.get('cps.errors.queryFailed') || '查询失败，请稍后再试';
      Toast.error(errorMessage);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setPhoneNumber(value);
    }
  };

  const handleOpenLogisticsModal = (order: any, orderItemId: string) => {
    setSelectedOrder(order);
    setOrderItemId(orderItemId);
    setShowLogisticsModal(true);
  };

  const handleCloseLogisticsModal = () => {
    setShowLogisticsModal(false);
    setSelectedOrder(null);
    setOrderItemId('');
  };

  return (
    <div className={[globalStyles['container'], styles['cps-container']].join(' ')}>
      <div className={styles['header']}>
        <div className={styles['title']}>
          {intl.get('cps.pageTitle')}
        </div>
      </div>

      {orders.length === 0 && (
        <div className={styles['input-section']}>
          <div className={styles['input-group']}>
            <input
              type="text"
              inputMode="numeric"
              className={styles['input']}
              placeholder={intl.get('cps.phonePlaceholder')}
              value={phoneNumber}
              onChange={handlePhoneInput}
              maxLength={4}
            />
          </div>
          <button
            className={styles['query-btn']}
            onClick={handleQuery}
            disabled={loading}
          >
            {loading ? intl.get('cps.querying') : intl.get('cps.queryButton')}
          </button>
        </div>
      )}

      <div className={styles['orders-section']}>
        {orders.map((order, index) => (
          <OrderCard 
            key={order.saOrderId || index}
            order={order}
            onViewLogistics={(orderItemId: string) => handleOpenLogisticsModal(order, orderItemId)}
          />
        ))}
      </div>

      {showLogisticsModal && selectedOrder && (
        <LogisticsTrackModal
          order={selectedOrder}
          orderItemId={orderItemId}
          onClose={handleCloseLogisticsModal}
          urlParams={urlParams}
        />
      )}
    </div>
  );
}

export default memo(CPSPage);
